import React from 'react';
import {
  Typography,
  Container,
  Paper,
  Divider,
  Button,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { SEOHead } from '../../components/SEO';

const PolicyContent = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  '& h2': {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  '& h3': {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    color: theme.palette.secondary.main,
  },
  '& p': {
    marginBottom: theme.spacing(1),
    lineHeight: 1.6,
  },
  '& ul': {
    marginLeft: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
}));

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fafafa', paddingTop: 32, paddingBottom: 32 }}>
      <SEOHead
        title="Privacy Policy"
        description="Learn how Prismatic Forge protects your privacy and handles your data. GDPR compliant with enterprise-grade security."
        canonical="https://prismaticforge.ai/privacy/"
        ogUrl="https://prismaticforge.ai/privacy/"
      />
      <Container maxWidth="md">
        <div style={{ marginBottom: 24 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            style={{ marginBottom: 16 }}
          >
            Back to Home
          </Button>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            style={{ fontWeight: 'bold', color: '#1976d2' }}
          >
            Privacy Policy
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Prismatic Forge Adaptive Productivity System
          </Typography>
        </div>

        <PolicyContent elevation={3}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Last Updated: January 1, 2025
          </Typography>

          <Typography variant="body1" paragraph style={{ fontWeight: 500, fontSize: '1.1rem' }}>
            At Prismatic Forge, we are committed to protecting your privacy and ensuring the security
            of your personal information. This Privacy Policy explains how we collect, use, store,
            and protect your data when you use our adaptive productivity system.
          </Typography>

          <Typography variant="h5" style={{ marginTop: 24, color: '#1976d2' }}>
            1. Information We Collect
          </Typography>

          <Typography variant="h6" style={{ color: '#9c27b0' }}>
            1.1 Information You Provide Directly
          </Typography>
          <Typography variant="body1" paragraph>
            We collect information you voluntarily provide when using our Service, including:
          </Typography>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, password, organization details</li>
            <li><strong>Profile Information:</strong> Job title, department, preferences, and settings</li>
            <li><strong>Content:</strong> Documents, notes, messages, and other content you create or upload</li>
            <li><strong>Communication:</strong> Messages you send to our support team or feedback you provide</li>
            <li><strong>Payment Information:</strong> Billing address and payment details (processed securely by our payment processor)</li>
          </ul>

          <Typography variant="h6" style={{ color: '#9c27b0' }}>
            1.2 Information We Collect Automatically
          </Typography>
          <Typography variant="body1" paragraph>
            When you use our Service, we automatically collect certain information:
          </Typography>
          <ul>
            <li><strong>Usage Data:</strong> Features used, time spent, interaction patterns, and productivity metrics</li>
            <li><strong>Technical Data:</strong> IP address, browser type, device information, operating system</li>
            <li><strong>Performance Data:</strong> Error logs, performance metrics, and system diagnostics</li>
            <li><strong>Analytics Data:</strong> Aggregated usage statistics to improve our Service</li>
          </ul>

          <Typography variant="h6" style={{ color: '#9c27b0' }}>
            1.3 Information from Third-Party Integrations
          </Typography>
          <Typography variant="body1" paragraph>
            With your explicit consent, we may access data from integrated services:
          </Typography>
          <ul>
            <li><strong>Microsoft 365:</strong> Email, calendar events, documents, Teams messages</li>
            <li><strong>Google Workspace:</strong> Gmail, Google Calendar, Drive files, Meet recordings</li>
            <li><strong>Other Integrations:</strong> Data from other productivity tools you connect</li>
          </ul>

          <Typography variant="h5" style={{ marginTop: 24, color: '#1976d2' }}>
            1.4 Google User Data
          </Typography>
          <Typography variant="body1" paragraph>
            When you connect your Google account to Prismatic Forge, we access the following Google user data
            with your explicit consent through Google&apos;s OAuth 2.0 authorization flow:
          </Typography>
          <ul>
            <li><strong>Basic Profile Information:</strong> Your name, email address, and profile picture from your Google account</li>
            <li><strong>Gmail Data:</strong> Email messages, email metadata (sender, recipient, subject, date), labels, and threads for the purpose of email search, analysis, and knowledge graph integration</li>
            <li><strong>Google Calendar Data:</strong> Calendar events, event details, and free/busy information for scheduling and awareness features</li>
            <li><strong>Google Drive Data:</strong> Document metadata, file names, and file contents for document analysis and knowledge management</li>
            <li><strong>Google Contacts:</strong> Contact names, email addresses, and organization information for relationship mapping</li>
          </ul>

          <Typography variant="h6" style={{ color: '#9c27b0' }}>
            How We Use Google User Data
          </Typography>
          <Typography variant="body1" paragraph>
            Prismatic Forge uses Google user data solely to provide and improve the core functionality of
            our application. Specifically, we use Google user data to:
          </Typography>
          <ul>
            <li>Authenticate your identity and provide secure access to the Service</li>
            <li>Sync and display your emails within the Prismatic Forge knowledge graph for search and discovery</li>
            <li>Provide AI-powered email analysis, communication pattern detection, and context-aware assistance through our AI teammates</li>
            <li>Integrate calendar events for scheduling awareness and daily briefings</li>
            <li>Analyze documents for knowledge extraction and semantic relationship building</li>
            <li>Map contacts and communication patterns to enhance your knowledge network</li>
          </ul>
          <Typography variant="body1" paragraph>
            <strong>We do NOT use Google user data for any of the following purposes:</strong>
          </Typography>
          <ul>
            <li>Targeted, personalized, retargeted, or interest-based advertising</li>
            <li>Selling data to third parties, data brokers, or information resellers</li>
            <li>Determining credit-worthiness or for lending purposes</li>
            <li>Training general-purpose AI or machine learning models unrelated to providing the Service to you</li>
            <li>Creating databases for sale or distribution to third parties</li>
            <li>Any purpose other than providing or improving Prismatic Forge&apos;s user-facing features</li>
          </ul>

          <Typography variant="h6" style={{ color: '#9c27b0' }}>
            Sharing, Transfer, and Disclosure of Google User Data
          </Typography>
          <Typography variant="body1" paragraph>
            We do not sell, rent, trade, or transfer Google user data to third parties. We do not disclose
            your Google user data to any third party except in the following limited circumstances:
          </Typography>
          <ul>
            <li><strong>Infrastructure Providers:</strong> We use cloud hosting and database services to securely store
            and process your data. These providers are bound by strict data processing agreements and do not
            have independent access to your Google user data.</li>
            <li><strong>Legal Requirements:</strong> We may disclose Google user data if required by law, court order,
            or governmental regulation, or to protect the rights, safety, or property of Prismatic Forge or its users.</li>
          </ul>
          <Typography variant="body1" paragraph>
            We do not transfer or disclose Google user data to third parties for advertising, data brokerage,
            information resale, credit assessment, or any purpose other than providing or improving
            Prismatic Forge&apos;s functionality.
          </Typography>

          <Typography variant="h6" style={{ color: '#9c27b0' }}>
            Protection of Google User Data
          </Typography>
          <Typography variant="body1" paragraph>
            We implement robust security measures to protect the confidentiality, integrity, and availability
            of your Google user data:
          </Typography>
          <ul>
            <li><strong>Encryption in Transit:</strong> All Google user data is transmitted using TLS 1.3 encryption</li>
            <li><strong>Encryption at Rest:</strong> All stored Google user data is encrypted using AES-256 encryption</li>
            <li><strong>Access Controls:</strong> Strict role-based access controls ensure that only authorized
            systems and personnel can access Google user data</li>
            <li><strong>Data Isolation:</strong> Google user data is isolated per user and per organization, preventing
            unauthorized cross-access</li>
            <li><strong>Security Monitoring:</strong> Continuous monitoring, intrusion detection, and regular security
            audits protect against unauthorized access</li>
            <li><strong>OAuth Token Security:</strong> Google OAuth tokens are stored securely and are never exposed
            to client-side code or unauthorized services</li>
          </ul>

          <Typography variant="h6" style={{ color: '#9c27b0' }}>
            Retention and Deletion of Google User Data
          </Typography>
          <Typography variant="body1" paragraph>
            We retain Google user data only for as long as necessary to provide the Service to you:
          </Typography>
          <ul>
            <li><strong>Active Account:</strong> Google user data is retained while your account is active and
            your Google integration is connected</li>
            <li><strong>Disconnection:</strong> When you disconnect your Google account from Prismatic Forge,
            we delete all synced Google user data from our systems within 30 days</li>
            <li><strong>Account Deletion:</strong> When you delete your Prismatic Forge account, all Google
            user data is permanently deleted within 30 days</li>
            <li><strong>User Request:</strong> You may request deletion of your Google user data at any time
            by contacting us at privacy@prismaticforge.ai or through your account settings. We will process
            deletion requests within 30 days.</li>
          </ul>
          <Typography variant="body1" paragraph>
            You may also revoke Prismatic Forge&apos;s access to your Google data at any time through your
            Google Account permissions page at{' '}
            <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer"
              style={{ color: '#1976d2' }}>
              https://myaccount.google.com/permissions
            </a>.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            2. How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We use your information for the following purposes:
          </Typography>
          <ul>
            <li><strong>Service Provision:</strong> To provide, maintain, and improve our adaptive productivity system</li>
            <li><strong>Personalization:</strong> To customize your experience and adapt the interface to your needs</li>
            <li><strong>AI Services:</strong> To power our AI teammates and provide intelligent assistance</li>
            <li><strong>Analytics:</strong> To understand usage patterns and improve our Service</li>
            <li><strong>Communication:</strong> To send you updates, notifications, and support messages</li>
            <li><strong>Security:</strong> To protect against fraud, abuse, and security threats</li>
            <li><strong>Compliance:</strong> To comply with legal obligations and respond to lawful requests</li>
          </ul>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            3. Data Storage and Security
          </Typography>
          <Typography variant="body1" paragraph>
            We implement industry-leading security measures to protect your data:
          </Typography>
          <ul>
            <li><strong>Encryption:</strong> All data is encrypted using AES-256 encryption at rest and TLS 1.3 in transit</li>
            <li><strong>Infrastructure:</strong> Data is stored in SOC 2 Type II compliant data centers</li>
            <li><strong>Access Controls:</strong> Strict access controls and multi-factor authentication for our systems</li>
            <li><strong>Monitoring:</strong> Continuous security monitoring and regular penetration testing</li>
            <li><strong>Backup:</strong> Regular encrypted backups to ensure data recovery capabilities</li>
          </ul>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            4. Data Sharing and Disclosure
          </Typography>
          <Typography variant="body1" paragraph>
            We do not sell, rent, or trade your personal data. We may share your information only in these limited circumstances:
          </Typography>
          <ul>
            <li><strong>Service Providers:</strong> Trusted third parties who assist in providing our Service (all bound by strict confidentiality agreements)</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect rights and safety</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with advance notice)</li>
            <li><strong>Consent:</strong> When you explicitly authorize us to share specific information</li>
          </ul>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            5. Your Rights and Choices
          </Typography>
          <Typography variant="body1" paragraph>
            Under various privacy laws (GDPR, CCPA, etc.), you have the following rights:
          </Typography>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
            <li><strong>Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
            <li><strong>Portability:</strong> Export your data in a machine-readable format</li>
            <li><strong>Restriction:</strong> Limit how we process your data</li>
            <li><strong>Objection:</strong> Object to certain types of processing</li>
            <li><strong>Consent Withdrawal:</strong> Withdraw consent for voluntary data processing</li>
          </ul>
          <Typography variant="body1" paragraph>
            To exercise these rights, contact us at privacy@prismaticforge.ai.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            6. Cookies and Tracking Technologies
          </Typography>
          <Typography variant="body1" paragraph>
            We use cookies and similar technologies for:
          </Typography>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for authentication, security, and basic functionality</li>
            <li><strong>Performance Cookies:</strong> To understand how you use our Service and improve performance</li>
            <li><strong>Functional Cookies:</strong> To remember your preferences and settings</li>
            <li><strong>Analytics Cookies:</strong> To gather insights about usage patterns (with your consent)</li>
          </ul>
          <Typography variant="body1" paragraph>
            You can manage cookie preferences through our cookie consent manager or your browser settings.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            7. Data Retention
          </Typography>
          <Typography variant="body1" paragraph>
            We retain your data for the following periods:
          </Typography>
          <ul>
            <li><strong>Account Data:</strong> For as long as your account is active</li>
            <li><strong>Usage Data:</strong> Up to 3 years for analytics and service improvement</li>
            <li><strong>Support Data:</strong> Up to 2 years for customer service purposes</li>
            <li><strong>Legal Compliance:</strong> As required by applicable laws (typically 7 years for business records)</li>
          </ul>
          <Typography variant="body1" paragraph>
            After account deletion, we may retain anonymized, aggregated data for analytics purposes.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            8. Children's Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            Our Service is not intended for users under 16 years of age. We do not knowingly collect
            personal information from children. If we become aware that we have collected data from
            a child without proper consent, we will delete it immediately.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            9. International Data Transfers
          </Typography>
          <Typography variant="body1" paragraph>
            If we transfer your data internationally, we ensure appropriate safeguards are in place,
            including Standard Contractual Clauses, adequacy decisions, or other approved transfer
            mechanisms under applicable privacy laws.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            10. Changes to This Privacy Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy periodically to reflect changes in our practices or
            legal requirements. We will notify you of material changes by email or through our Service.
            Your continued use of the Service after notification constitutes acceptance of the updated policy.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            11. Contact Information
          </Typography>
          <Typography variant="body1" paragraph>
            For questions about this Privacy Policy or to exercise your privacy rights, please contact us:
          </Typography>
          <Typography variant="body1" component="div">
            <strong>Privacy Officer:</strong> privacy@prismaticforge.ai<br />
            <strong>Data Protection Officer:</strong> dpo@prismaticforge.ai<br />
            <strong>General Support:</strong> support@prismaticforge.ai<br />
            <strong>Address:</strong> Prismatic Forge, Inc.<br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;100 Innovation Drive<br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;San Francisco, CA 94105
          </Typography>

          <Typography variant="body1" paragraph style={{ marginTop: 24 }}>
            <strong>EU Representative:</strong> For users in the European Union, our representative is:<br />
            Prismatic Forge EU Privacy Services<br />
            privacy-eu@prismaticforge.ai
          </Typography>

          <Divider style={{ marginTop: 32, marginBottom: 32 }} />

          <Typography variant="body2" color="text.secondary" style={{ textAlign: 'center' }}>
            This Privacy Policy is effective as of January 1, 2025
          </Typography>
        </PolicyContent>
      </Container>
    </div>
  );
};

export default PrivacyPolicy;